// Edge-compatible GraphQL queries (plain strings instead of gql template literals)

export const GET_BUSINESS_VIA_IDENTIFIER = `
  query Business($bookingUrlIdentifier: String!) {
    business(bookingUrlIdentifier: $bookingUrlIdentifier) {
      about
      acceptsOnlineBookings
      bookingManagement 
      bookingUrlIdentifier
      showPromosOnBookingSite
      giftCardSettingForBooking {
        id
        name
        description
        enabled
        minAmount
        maxAmount
        online
        inStore
      }
      callingCode
      cancellationPolicy
      countryCode
      timezone
      termsAndConditions
      headerImageUrl
      headerImageUrls {
        id
        imageUrl
      }
      id
      allowServiceStaffSelection
      singleStaffBooking
      selectAnyStaffForOnlineBooking
      logoUrl
      name
      ownerId
      phone
      slug
      businessSpecialties {
        id
        name
        description
      }
      country {
        name
        code
        emojiFlag
        collectionsSupported
        payoutsSupported
        timezone
        utcOffset
        currency {
          code
          symbol
        }
      }
      salons(bookingVisible: true) {
        address
        bearsProcessingFee
        branchName
        callingCode
        countryCode
        city
        email
        id
        isTaxVisible
        locationType
        phone
        state
        status
        urlIdentifier
        bookingLink
        taxInclusivePricing
        packages {
          activateTax
          customPrice
          description
          id
          isDiscountApplicable
          isPurchasableAsVoucher
          name
          salonId
          allowSaleOn
          status
          totalPrice
          usesCustomPrice
          validityMonths
          packageServices {
            duration
            id
            name
            packageId
            price
            quantity
            serviceId
          }
        }
      }
    }
  }
`;

export const GET_SALON_FOR_BOOKING = `
  query SalonForBooking($salonId: ID!) {
    salonForBooking(salonId: $salonId) {
      address
      bearsProcessingFee
      branchName
      callingCode
      countryCode
      city
      email
      id
      isTaxVisible
      locationType
      phone
      state
      status
      taxInclusivePricing
      bookingLink
      salonStaffs {
        role
        id
        active
        canServeCustomers
        user {
          firstName
          fullName
          lastName
        }
        specialty
      }
      services {
        customerCanSchedule
        description
        duration
        id
        isDepositRequired
        name
        isPublic
        featured
        featuredIndex
        price
        pricingType
        salonId
        hasVariants
        serviceVariants {
          active
          addedById
          createdAt
          deletedAt
          depositType
          depositValue
          description
          duration
          id
          isDepositRequired
          name
          price
          service {
            id
          }
          serviceId
          sortOrder
          updatedAt
        }
        category {
          createdAt
          id
          name
          updatedAt
          orderIndex
        }
        serviceStaffs {
          id
          serviceId
          price
          salonStaff {
            id
            active
            specialty
            role
            user {
              fullName
              firstName
              lastName
            }
          }
        }
      }
      salonHours {
        closeTime
        createdAt
        day
        id
        openTime
        updatedAt
      }
      closedPeriods {
        id
        title
        startAt
        endAt
      }
    }
  }
`;

export const GET_FAQS = `
  query GetFaqs($salonId: ID!) {
    faqs(salonId: $salonId) {
      id
      question
      answer
      businessId
      createdAt
      updatedAt
    }
  }
`;

export const SEARCH_CLIENT = `
  query ClientSearch($salonId: ID!, $phone: String!, $countryCode: String!) {
    clientSearch(salonId: $salonId, phone: $phone, countryCode: $countryCode) {
      id
      businessId
      salonId
      createdAt
      updatedAt
      firstName
      lastName
      email
      phone
      callingCode
      countryCode
      address
      walletBalance
      membershipWalletBalance
      blacklisted
      clientMembership {
        id
        membershipStatus
        membership {
          id
          name
          rewardPercentage
          services {
            id
            name
          }
        }
      }
    }
  }
`;

export const CREATE_CLIENT = `
  mutation CreateClient($input: CreateClientInput!) {
    createClient(input: $input) {
      client {
        id
        businessId
        salonId
        firstName
        lastName
        email
        phone
        callingCode
        countryCode
        address
      }
      errors {
        message
        property
      }
    }
  }
`;

export const REDEEM_VOUCHER_CODE = `
   mutation RedeemVoucherCode($input: RedeemVoucherCodeInput!) {
    redeemVoucherCode(input: $input) {
      clientMutationId
      status
      errors {
        message
        property
      }
      voucher {
        amountRedeemed
        clientId
        code
        createdAt
        expiresAt
        id
        isPaymentConfirmed
        lastUsageAt
        price
        redemptionMode
        reference
        salonId
        status
        updatedAt
        validityMonths
        services {
          id
          name
          price
          duration
        }
        client {
          addedById
          address
          amountSpent
          businessId
          createdAt
          deletedAt
          dob
          id
          lastVisit
          pointsEarned
          salonId
          updatedAt
          altCallingCode
          altCountryCode
          altPhone
          callingCode
          countryCode
          email
          firstName
          lastName
          phone
        }
        voucherServices {
          createdAt
          duration
          id
          isRedeemed
          name
          quantity
          packageVoucherId
          price
          redeemedCount
          serviceId
          updatedAt
        }
      }
    }
  }
`;

export const CREATE_APPOINTMENT = `
  mutation CreateAppointment($input: CreateAppointmentInput!) {
    createAppointment(input: $input) {
      status
      appointment {
        id
        bookingId
        appointmentServices {
          id
          name
          price
          duration
          membershipDiscountAmount
        }
      }
      paymentAuthorization {
        accessCode
        reference
        authorizationUrl
      }
      initiatePayment
      amountToPay
      processingFeeAmount
      errors {
        message
      }
    }
  }
`;

export const DELETE_APPOINTMENT = `
  mutation DeleteAppointment($salonId: ID!, $id: ID!) {
    deleteAppointment(input: { salonId: $salonId, id: $id, safeMode: true }) {
      status
      errors {
        message
      }
    }
  }
`;

export const APPOINTMENT_PREVIEW = `
  mutation AppointmentPreview($input: AppointmentPreviewInput!) {
    appointmentPreview(input: $input) {
      clientMutationId
      status
      data {
        addOnAmount
        depositAmount
        cancellationFeeMessages
        processingFeeAmount
        depositProcessingFeeAmount
        taxAmount
        subTotal
        totalPaid
        totalPoints
        totalPrice
        totalProductsAmount
        totalServicesAmount
        loyaltyDiscountAmount
        discountAmount
        cancellationFee
        cancellationFeeMessage
        appointmentPromo {
          amount
          title
        }
        feedbackMessages {
          message
          type
        }
      }
      errors {
        message
        property
      }
    }
  }
`;

export const GET_APPOINTMENT_SUMMARY = `
  query GetAppointmentSummary($id: String!) {
    appointmentSummary(id: $id) {
      id
      appointmentDate
      appointmentTime
      status
      totalAmount
      salonId
      clientId
      services {
        id
        name
        price
        duration
      }
      staff {
        id
        firstName
        lastName
      }
    }
  }
`;

export const SALON_AVAILABILITY = `
  query SalonAvailability($salonId: ID!, $services: [AvailabilityInput!]!, $startDate: String!, $endDate: String!) {
    salonAvailability(
      salonId: $salonId
      services: $services
      startDate: $startDate
      endDate: $endDate
    ) {
      date
      reason
      results {
        startTime
        endTime
      }
    }
  }
`;

export const GET_SERVICE_STAFFS = `
  query ServiceStaffs($serviceIds: [ID!]!) {
    serviceStaffs(serviceIds: $serviceIds) {
      salonStaffsForBooking {
        active
        canServeCustomers
        id
        role
        specialty
        salons {
          id
        }
        user {
          firstName
          fullName
          id
          lastName
        }
      }
    }
  }
`;

export const VALIDATE_PHONE_NUMBER = `
  query ValidatePhoneNumber($countryCode: String!, $phoneNumber: String!) {
    validatePhoneNumber(countryCode: $countryCode, phoneNumber: $phoneNumber) {
      numberValid
    }
  }
`;
